/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a state, column, header or event code; it renders nothing */
// C4.6 Deck edits for the web: the deck DRAFT (mutable, durable, one per
// owner and deck, in the owner's fenced `drafts` store) and the ATTEMPT (an
// immutable C4 record, kind deck_edit, in `queue`).
//
//  - Every offline edit rewrites the draft in place (coalescing: ten offline
//    edits are one draft). A draft equal to its base, with no unsettled
//    attempt, is cleared.
//  - An attempt is built only by the dispatcher, online, when the deck has a
//    dirty draft and no unsettled attempt: read the owner's row; missing ->
//    "deleted elsewhere"; rev != base_rev -> the "Your deck changed" conflict;
//    else freeze a guarded PostgREST PATCH (`rev=eq.N`, return=representation)
//    of real saved_decks columns built onto THIS fresh read.
//  - Done ONLY on one returned row with rev > N holding the payload. A
//    zero-row 2xx and a lost response settle by reading the row: rev == N and
//    not ours -> not applied, resend the SAME request; rev > N and ours ->
//    done; rev > N and not ours -> conflict; missing -> failed (draft kept).
//  - Only `done` rebases the draft onto the ACKNOWLEDGED row and compares;
//    different -> a successor from that rev, equal -> the draft is cleared.
//    A conflict or a missing row never rebases and never creates a successor.
import { rfc3339 } from './time'
import {
  CONTENT_TYPE_JSON, HOLD, METHOD, OP_KIND, OP_STATE, OUTCOME, PREFER_REPRESENTATION, SCHEMA_VERSION, SERVICE, STORE,
} from './offline.codes'
import { opOutcome, type HttpExchange } from './httpOutcome'
import { backoffDelayMs } from './schedulerCore'
import { fencedWrite, ownerGetAll, type FencedResult, type WriterCtx } from './offlineStore'
import { newOpId, readQueue, requestText, utf8ToBase64, type DispatchCurrent, type OpRecord, decideHead } from './opQueue'
import { ownerSubject } from './identityMachine'
import { canonicalCardMeta, mergeCardMetaDelta, parseCardMeta, pruneCardMetaForText, storedCardMeta, type CardMetaMap } from '../cardMeta'
import { countDecklistCards } from '../decklist'
import { revOf, rowHoldsPayload } from '../deckRowPayload'
import { zoneWriteFor } from '../../components/decks/editor/commandZoneSwap'

export interface CardMetaDelta {
  base: CardMetaMap
  next: CardMetaMap
  no_prune?: boolean
}

export interface DeckDraft {
  schema_version: number
  deck_id: string
  owner_key: string
  owner_incarnation: number
  /** The last ACKNOWLEDGED server state (null rev: unknown, compared by text). */
  base_rev: number | null
  base_text: string | null
  base_card_meta: unknown
  /** The latest desired state. */
  text: string
  card_meta_delta: CardMetaDelta | null
  updated_at: string
  /**
   * Waiting for the user's explicit choice: the row moved past the base
   * (conflict), is gone (missing), or the server refused the write for good
   * (failed). Never cleared, rebased or deleted without that choice.
   */
  status?: 'conflict' | 'missing' | 'failed' | null
  /** failed: what the server answered. */
  last_error?: OpRecord['last_error']
  /** The row the conflict was found against. */
  conflict_row?: Record<string, unknown> | null
}

export type DeckRow = Record<string, unknown>

/** Every event names the owner and incarnation it was committed for (finding 10). */
export type OutboxEventBody =
  | { type: 'done'; deckId: string; row: DeckRow; cleared: boolean }
  | { type: 'conflict'; deckId: string; row: DeckRow }
  | { type: 'missing'; deckId: string }
  | { type: 'failed'; deckId: string }
export type OutboxEvent = { ownerKey: string; incarnation: number } & OutboxEventBody

const listeners = new Set<(e: OutboxEvent) => void>()

export function subscribeDeckOutbox(fn: (e: OutboxEvent) => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

function emit(e: OutboxEvent): void {
  for (const l of [...listeners]) {
    try { l(e) } catch { /* a listener's failure never stops the drain */ }
  }
}

// ---- Pure helpers ----------------------------------------------------------------------------

export function isDeckAttempt(r: OpRecord, deckId?: string): boolean {
  return r.kind === OP_KIND.deckEdit && (deckId === undefined || r.scope.id === deckId)
}

/** At most one unsettled attempt per deck (C4 inv. 11). */
export function unsettledAttempt(queue: OpRecord[], deckId: string): OpRecord | null {
  return queue.find(r => isDeckAttempt(r, deckId) && r.state !== OP_STATE.done && r.state !== OP_STATE.failedPermanent) ?? null
}

/** The card_meta the draft wants stored on top of `rowMeta`, or undefined for no change; null = conflict. */
function desiredMeta(draft: DeckDraft, rowMeta: unknown): CardMetaMap | null | undefined {
  if (!draft.card_meta_delta) return undefined
  const merged = mergeCardMetaDelta(parseCardMeta(rowMeta ?? null), draft.card_meta_delta.base, draft.card_meta_delta.next)
  if (merged.conflicts.length > 0) return null
  const want = draft.card_meta_delta.no_prune ? merged.meta : pruneCardMetaForText(merged.meta, draft.text)
  return storedCardMeta(want) ?? {}
}

/** The draft's desired content is exactly what `row` holds. */
export function draftSatisfiedBy(draft: DeckDraft, row: DeckRow): boolean {
  if ((row.decklist ?? null) !== draft.text) return false
  const meta = desiredMeta(draft, row.card_meta)
  if (meta === null) return false
  if (meta === undefined) return true
  return canonicalCardMeta(meta) === canonicalCardMeta(parseCardMeta(row.card_meta ?? null))
}

export type BuildResult =
  | { kind: 'payload'; payload: Record<string, unknown> }
  | { kind: 'noop' }
  | { kind: 'conflict' }

/** The instant save's columns (decklist, card_count, the zone when it moved, card_meta merged onto THIS read). */
export function buildDeckPayload(draft: DeckDraft, row: DeckRow): BuildResult {
  const payload: Record<string, unknown> = { decklist: draft.text, card_count: countDecklistCards(draft.text) }
  const zone = zoneWriteFor(draft.text, (row.decklist as string | null | undefined) ?? null)
  if (zone) {
    payload.commander = zone.commander
    payload.partner = zone.partner
  }
  const meta = desiredMeta(draft, row.card_meta)
  if (meta === null) return { kind: 'conflict' }
  if (meta !== undefined) payload.card_meta = Object.keys(meta).length ? meta : null
  if (rowHoldsPayload(row, payload)) return { kind: 'noop' }
  return { kind: 'payload', payload }
}

function sortedJson(payload: Record<string, unknown>): string {
  const out: Record<string, unknown> = {}
  for (const k of Object.keys(payload).sort()) out[k] = payload[k]
  return JSON.stringify(out)
}

/**
 * The guarded PATCH path the in-app save sends (useSavedDecks guardedUpdate):
 * the rev guard, and the decklist guard the editor's save always carries
 * (`expectedDecklist`: the base text, `is.null` for a list-less row).
 */
export function attemptPath(deckId: string, userId: string, rev: number, baseText?: string | null): string {
  const guard = baseText === undefined ? '' : baseText === null ? '&decklist=is.null' : `&decklist=eq.${encodeURIComponent(baseText)}`
  return `/rest/v1/saved_decks?id=eq.${encodeURIComponent(deckId)}&user_id=eq.${encodeURIComponent(userId)}&rev=eq.${rev}${guard}&select=*`
}

export function freezeAttempt(draft: DeckDraft, row: DeckRow, payload: Record<string, unknown>, now: Date, newId: () => string = newOpId): OpRecord {
  const rev = revOf(row) ?? 0
  const userId = ownerSubject(draft.owner_key) ?? ''
  return {
    schema_version: SCHEMA_VERSION,
    op_id: newId(),
    kind: OP_KIND.deckEdit,
    owner_key: draft.owner_key,
    owner_incarnation: draft.owner_incarnation,
    scope: { type: 'deck', id: draft.deck_id },
    destination: { service: SERVICE.supabase, method: METHOD.patch, path: attemptPath(draft.deck_id, userId, rev, (row.decklist as string | null | undefined) ?? null), headers: { Prefer: PREFER_REPRESENTATION } },
    request: { content_type: CONTENT_TYPE_JSON, body: utf8ToBase64(sortedJson(payload)) },
    op_key: null,
    group: null,
    // Never sent: what the client needs to settle it (the payload itself is the request body).
    // base_text: the decklist guard's text (null: is.null), so a row whose
    // text no longer matches it is a conflict, never a resend that can not apply.
    meta: { attempt_id: newId(), base_rev: rev, base_text: (row.decklist as string | null | undefined) ?? null, draft_text: draft.text, draft_updated_at: draft.updated_at },
    created_at: rfc3339(now),
    state: OP_STATE.pending,
    hold: null,
    attempts: 0,
    last_attempt_at: null,
    next_attempt_at: null,
    last_error: null,
    receipt: null,
    local_refs: [],
  }
}

export type Settlement = 'done' | 'not_applied' | 'conflict' | 'missing'

/** The columns the attempt writes: its own frozen bytes. */
export function attemptPayload(record: OpRecord): Record<string, unknown> {
  try {
    const p = JSON.parse(requestText(record)) as unknown
    return p && typeof p === 'object' && !Array.isArray(p) ? (p as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/** A read row against an attempt (spec C4.6 settlement table). */
export function settleByRow(record: OpRecord, row: DeckRow | null): Settlement {
  if (!row) return 'missing'
  const n = record.meta.base_rev as number
  const rev = revOf(row) ?? -1
  const holds = rowHoldsPayload(row, attemptPayload(record))
  if (rev > n && holds) return 'done'
  if (rev > n) return 'conflict'
  // The decklist guard can never match this row again (its text moved
  // without a rev bump): the user decides, the PATCH is not resent forever.
  if (rev === n && 'base_text' in record.meta && (row.decklist ?? null) !== record.meta.base_text) return 'conflict'
  if (rev === n && !holds) return 'not_applied'
  // rev == N holding the payload (a no-op the server never counted): not applied either.
  return 'not_applied'
}

/** Done check of a PATCH response: exactly one row, rev > N, holding the payload. */
export function patchDone(record: OpRecord, body: unknown): DeckRow | null {
  if (!Array.isArray(body) || body.length !== 1) return null
  const row = body[0] as DeckRow
  const n = record.meta.base_rev as number
  const rev = revOf(row) ?? -1
  return rev > n && rowHoldsPayload(row, attemptPayload(record)) ? row : null
}

// ---- Storage --------------------------------------------------------------------------------

export function readDrafts(ownerKey: string): Promise<DeckDraft[]> {
  return ownerGetAll<DeckDraft>(ownerKey, STORE.drafts)
}

export interface EditInput {
  deckId: string
  /** The acknowledged base the editor knows (used only when no draft exists yet). */
  baseRev: number | null
  baseText: string | null
  baseCardMeta?: unknown
  text: string
  cardMetaDelta?: CardMetaDelta | null
}

function hasMetaChange(d: CardMetaDelta | null | undefined): boolean {
  return !!d && canonicalCardMeta(d.base) !== canonicalCardMeta(d.next)
}

/** Attempts the user's explicit resolution retires: held on a conflict, or failed. */
function stoppedAttempt(r: OpRecord, deckId: string): boolean {
  return isDeckAttempt(r, deckId) && (r.hold?.reason === HOLD.conflict || r.state === OP_STATE.failedPermanent)
}

function rebaseOnto(d: DeckDraft, row: DeckRow, now: Date): DeckDraft {
  return {
    ...d, base_rev: revOf(row) ?? null, base_text: (row.decklist as string | null) ?? null, base_card_meta: row.card_meta ?? null,
    status: null, conflict_row: null, last_error: null, updated_at: rfc3339(now),
  }
}

/**
 * Record an edit: ONE fenced readwrite on drafts + queue. The base of an
 * existing draft is kept (the last acknowledged state the outbox knows). A
 * stopped draft keeps its stop: only the user's explicit choice (keep mine,
 * try again, discard) clears it, never an inference from the edit's base
 * revision (r2 #4: a same-revision conflict has an unchanged rev). Cleared
 * when equal to its base with no unsettled attempt and no stop. `guard`: C2
 * re-checked inside.
 */
export function recordDeckEdit(ctx: WriterCtx, input: EditInput, now: Date, guard?: () => boolean): Promise<FencedResult> {
  return fencedWrite(ctx, [STORE.drafts, STORE.queue], tx => {
    const drafts = tx.objectStore(STORE.drafts)
    const qs = tx.objectStore(STORE.queue)
    const g = drafts.get(input.deckId)
    g.onsuccess = () => {
      const cur = g.result as DeckDraft | undefined
      const q = qs.getAll()
      q.onsuccess = () => {
        const queue = (q.result as OpRecord[]) ?? []
        const next: DeckDraft = cur
          ? { ...cur, text: input.text, card_meta_delta: input.cardMetaDelta ?? cur.card_meta_delta, updated_at: rfc3339(now) }
          : {
              schema_version: SCHEMA_VERSION, deck_id: input.deckId, owner_key: ctx.ownerKey, owner_incarnation: ctx.incarnation,
              base_rev: input.baseRev, base_text: input.baseText, base_card_meta: input.baseCardMeta ?? null,
              text: input.text, card_meta_delta: input.cardMetaDelta ?? null, updated_at: rfc3339(now), status: null, conflict_row: null,
            }
        const attempt = unsettledAttempt(queue.filter(r => !stoppedAttempt(r, input.deckId)), input.deckId)
        const clean = next.text === (next.base_text ?? '') && !hasMetaChange(next.card_meta_delta)
        if (clean && !attempt && !next.status) drafts.delete(input.deckId)
        else drafts.put(next, input.deckId)
      }
    }
  }, guard)
}

/** The user dismissed a draft (Discard / Use current / deleted elsewhere): the draft and its stopped attempts go. */
export function discardDeckDraft(ctx: WriterCtx, deckId: string): Promise<FencedResult> {
  return fencedWrite(ctx, [STORE.drafts, STORE.queue], tx => {
    tx.objectStore(STORE.drafts).delete(deckId)
    const qs = tx.objectStore(STORE.queue)
    const q = qs.getAll()
    q.onsuccess = () => {
      for (const r of (q.result as OpRecord[]) ?? []) if (stoppedAttempt(r, deckId)) qs.delete(r.op_id)
    }
  })
}

/** "Keep my version" on a conflict: rebased on the row the conflict was found against; the next attempt is built from it. */
export function keepMineDeckDraft(ctx: WriterCtx, deckId: string, now: Date, guard?: () => boolean): Promise<FencedResult> {
  return fencedWrite(ctx, [STORE.drafts, STORE.queue], tx => {
    const ds = tx.objectStore(STORE.drafts)
    const qs = tx.objectStore(STORE.queue)
    const g = ds.get(deckId)
    g.onsuccess = () => {
      const d = g.result as DeckDraft | undefined
      if (!d || d.status !== 'conflict' || !d.conflict_row) return
      ds.put({ ...rebaseOnto(d, d.conflict_row, now), text: d.text }, deckId)
      const q = qs.getAll()
      q.onsuccess = () => { for (const r of (q.result as OpRecord[]) ?? []) if (stoppedAttempt(r, deckId)) qs.delete(r.op_id) }
    }
  }, guard)
}

/** "Try again" after a permanent failure: the failed attempt goes and the draft runs again. */
export function retryDeckDraft(ctx: WriterCtx, deckId: string, guard?: () => boolean): Promise<FencedResult> {
  return fencedWrite(ctx, [STORE.drafts, STORE.queue], tx => {
    const ds = tx.objectStore(STORE.drafts)
    const qs = tx.objectStore(STORE.queue)
    const g = ds.get(deckId)
    g.onsuccess = () => {
      const d = g.result as DeckDraft | undefined
      if (!d || d.status !== 'failed') return
      ds.put({ ...d, status: null, last_error: null }, deckId)
      const q = qs.getAll()
      q.onsuccess = () => { for (const r of (q.result as OpRecord[]) ?? []) if (stoppedAttempt(r, deckId)) qs.delete(r.op_id) }
    }
  }, guard)
}

/** The user's "Your deck changed" choice from the editor. 'theirs' = discard; 'mine' = keep mine on `row`. */
export function resolveDeckConflict(ctx: WriterCtx, deckId: string, choice: 'mine' | 'theirs', row: DeckRow, now: Date): Promise<FencedResult> {
  if (choice === 'theirs') return discardDeckDraft(ctx, deckId)
  return fencedWrite(ctx, [STORE.drafts, STORE.queue], tx => {
    const ds = tx.objectStore(STORE.drafts)
    const qs = tx.objectStore(STORE.queue)
    const g = ds.get(deckId)
    g.onsuccess = () => {
      const d = g.result as DeckDraft | undefined
      if (!d) return
      ds.put({ ...rebaseOnto(d, row, now), text: d.text }, deckId)
      const q = qs.getAll()
      q.onsuccess = () => {
        for (const r of (q.result as OpRecord[]) ?? []) if (isDeckAttempt(r, deckId) && r.state !== OP_STATE.done) qs.delete(r.op_id)
      }
    }
  })
}

// ---- The dispatcher ---------------------------------------------------------------------------

export interface DeckTransport {
  /** GET the owner's row (select=*, id and user_id filters). */
  readRow: (deckId: string, userId: string) => Promise<{ ok: true; row: DeckRow | null } | { ok: false; exchange: HttpExchange }>
  /** Send the frozen PATCH (exact bytes). */
  patch: (record: OpRecord) => Promise<{ exchange: HttpExchange; body: unknown }>
}

export interface DeckDrainDeps {
  ctx: WriterCtx
  current: () => DispatchCurrent
  transport: DeckTransport
  now: () => Date
  u: () => number
  signal?: AbortSignal
  newId?: () => string
}

function stillOwned(deps: DeckDrainDeps, r: OpRecord): boolean {
  if (deps.signal?.aborted) return false
  const d = decideHead({ ...r, state: OP_STATE.pending, next_attempt_at: null, hold: null }, deps.current(), deps.now().getTime())
  return d.decision === 'dispatch'
}

/** What one settlement does, decided INSIDE its transaction from the latest draft and queue. */
interface SettlePlan {
  putRecords?: OpRecord[]
  delRecords?: string[]
  /** undefined: unchanged; null: delete; a draft: put. */
  draft?: DeckDraft | null
  event?: OutboxEvent
}

/**
 * ONE fenced transaction over drafts + queue (finding 6): the latest draft
 * and queue are read inside it and `decide` plans the change from them, so
 * a newer edit (another tab) is never overwritten or deleted. The event is
 * emitted only after the transaction COMMITTED (finding 10).
 */
async function settle(deps: DeckDrainDeps, deckId: string, decide: (latest: DeckDraft | null, queue: OpRecord[]) => SettlePlan): Promise<boolean> {
  let plan: SettlePlan = {}
  const res = await fencedWrite(deps.ctx, [STORE.queue, STORE.drafts], tx => {
    const qs = tx.objectStore(STORE.queue)
    const ds = tx.objectStore(STORE.drafts)
    const g = ds.get(deckId)
    g.onsuccess = () => {
      const latest = (g.result as DeckDraft | undefined) ?? null
      const q = qs.getAll()
      q.onsuccess = () => {
        plan = decide(latest, (q.result as OpRecord[]) ?? [])
        for (const r of plan.putRecords ?? []) qs.put(r, r.op_id)
        for (const id of plan.delRecords ?? []) qs.delete(id)
        if (plan.draft === null) ds.delete(deckId)
        else if (plan.draft) ds.put(plan.draft, deckId)
      }
    }
  })
  if (res !== 'ok') return false
  if (plan.event) emit(plan.event)
  return true
}

/** Drain every deck: one unsettled attempt at a time per deck. Returns the number of requests sent. */
export async function drainDeckEdits(deps: DeckDrainDeps): Promise<number> {
  let sent = 0
  const owner = deps.ctx.ownerKey
  const userId = ownerSubject(owner)
  if (!userId) return 0
  const drafts = await readDrafts(owner)
  const queue = (await readQueue(owner)).filter(r => isDeckAttempt(r))
  const deckIds = new Set([...drafts.map(d => d.deck_id), ...queue.filter(r => r.state !== OP_STATE.done && r.state !== OP_STATE.failedPermanent).map(r => r.scope.id)])
  for (const deckId of deckIds) {
    if (deps.signal?.aborted) break
    for (let guard = 0; guard < 6; guard++) {
      const step = await stepDeck(deps, deckId, userId)
      sent += step.sent
      if (!step.again) break
    }
  }
  return sent
}

interface Step { sent: number; again: boolean }

function ev(deps: DeckDrainDeps, e: OutboxEventBody): OutboxEvent {
  return { ...e, ownerKey: deps.ctx.ownerKey, incarnation: deps.ctx.incarnation }
}

async function stepDeck(deps: DeckDrainDeps, deckId: string, userId: string): Promise<Step> {
  const owner = deps.ctx.ownerKey
  const draft = (await readDrafts(owner)).find(d => d.deck_id === deckId) ?? null
  const queue = await readQueue(owner)
  const attempt = unsettledAttempt(queue, deckId)
  if (attempt) return runAttempt(deps, attempt, userId)
  // A stopped draft (conflict, missing, failed) or a failed attempt waits
  // for the user: never a new attempt on its own (finding 7).
  if (!draft || draft.status || queue.some(r => stoppedAttempt(r, deckId))) return { sent: 0, again: false }
  // Build: read the row, then freeze from THIS read (and the latest draft).
  const probe: OpRecord = { ...freezeAttempt(draft, { rev: draft.base_rev ?? 0 }, {}, deps.now(), () => 'probe') }
  if (!stillOwned(deps, probe)) return { sent: 0, again: false }
  const read = await deps.transport.readRow(deckId, userId)
  if (!stillOwned(deps, probe)) return { sent: 0, again: false }
  if (!read.ok) return { sent: 0, again: false }
  const row = read.row
  const now = deps.now()
  let froze = false
  const ok = await settle(deps, deckId, (latest, q) => {
    if (!latest || latest.status || unsettledAttempt(q, deckId) || q.some(r => stoppedAttempt(r, deckId))) return {}
    if (!row) return { draft: { ...latest, status: 'missing' }, event: ev(deps, { type: 'missing', deckId }) }
    const baseMatches = latest.base_rev !== null ? revOf(row) === latest.base_rev : (row.decklist ?? null) === latest.base_text
    if (!baseMatches) {
      // Another writer already stored exactly what we want: nothing to send.
      if (draftSatisfiedBy(latest, row)) return { draft: null, event: ev(deps, { type: 'done', deckId, row, cleared: true }) }
      return { draft: { ...latest, status: 'conflict', conflict_row: row }, event: ev(deps, { type: 'conflict', deckId, row }) }
    }
    const built = buildDeckPayload(latest, row)
    if (built.kind === 'conflict') return { draft: { ...latest, status: 'conflict', conflict_row: row }, event: ev(deps, { type: 'conflict', deckId, row }) }
    if (built.kind === 'noop') {
      const rebased = rebaseOnto(latest, row, now)
      return draftSatisfiedBy(rebased, row)
        ? { draft: null, event: ev(deps, { type: 'done', deckId, row, cleared: true }) }
        : { draft: rebased }
    }
    froze = true
    // Durable before it leaves (C4 inv. 1).
    return { putRecords: [freezeAttempt(latest, row, built.payload, now, deps.newId)] }
  })
  return { sent: 0, again: ok && froze }
}

async function runAttempt(deps: DeckDrainDeps, rec: OpRecord, userId: string): Promise<Step> {
  const now = deps.now()
  const head = decideHead(rec, deps.current(), now.getTime())
  if (head.decision === 'hold') {
    if (rec.hold?.reason !== head.reason && head.reason !== HOLD.conflict) {
      await settle(deps, rec.scope.id, (_l, q) => (q.some(r => r.op_id === rec.op_id) ? { putRecords: [{ ...rec, hold: { reason: head.reason, since: rfc3339(now) } }] } : {}))
    }
    return { sent: 0, again: false }
  }
  if (head.decision === 'not_due' || head.decision === 'skip' || head.decision === 'wait') return { sent: 0, again: false }
  if (head.decision === 'read_row') return settleRead(deps, rec, userId)
  // dispatch: in_flight persisted BEFORE the request leaves.
  const inflight: OpRecord = { ...rec, state: OP_STATE.inFlight, dispatched_from: rec.state, hold: null, attempts: rec.attempts + 1, last_attempt_at: rfc3339(now) }
  if (!stillOwned(deps, rec)) return { sent: 0, again: false }
  const marked = await settle(deps, rec.scope.id, (_l, q) => {
    const cur = q.find(r => r.op_id === rec.op_id)
    return cur && cur.state === rec.state ? { putRecords: [inflight] } : {}
  })
  if (!marked) return { sent: 0, again: false }
  if (!stillOwned(deps, rec)) {
    await settle(deps, rec.scope.id, () => ({ putRecords: [{ ...rec, hold: { reason: HOLD.awaitingSession, since: rfc3339(now) } }] }))
    return { sent: 0, again: false }
  }
  const res = await deps.transport.patch(inflight)
  const outcome = opOutcome(res.exchange)
  if (outcome === OUTCOME.done) {
    const row = patchDone(inflight, res.body)
    if (row) {
      const s = await onDone(deps, inflight, row)
      return { sent: 1 + s.sent, again: s.again }
    }
    // A zero-row 2xx is never done: settle by the read.
    const amb: OpRecord = { ...inflight, state: OP_STATE.ambiguous, dispatched_from: null }
    if (!(await settle(deps, rec.scope.id, () => ({ putRecords: [amb] })))) return { sent: 1, again: false }
    const s = await settleRead(deps, amb, userId)
    return { sent: 1 + s.sent, again: s.again }
  }
  const from = inflight.dispatched_from === OP_STATE.ambiguous ? OP_STATE.ambiguous : OP_STATE.pending
  const n = (rec.backoff_n ?? 0) + 1
  const retryAfter = res.exchange.kind === 'answered' ? res.exchange.retryAfterS : null
  const at = rfc3339(new Date(now.getTime() + backoffDelayMs(n, deps.u(), retryAfter)))
  const status = res.exchange.kind === 'answered' ? res.exchange.status : undefined
  let next: OpRecord
  let stop = false
  switch (outcome) {
    case OUTCOME.needsSession:
      next = { ...inflight, state: from, attempts: rec.attempts, dispatched_from: null, hold: { reason: HOLD.awaitingSession, since: rfc3339(now) } }
      break
    case OUTCOME.transient:
      next = { ...inflight, state: from, dispatched_from: null, backoff_n: n, next_attempt_at: at, last_error: { kind: res.exchange.kind, status } }
      break
    case OUTCOME.ambiguous:
      next = { ...inflight, state: OP_STATE.ambiguous, dispatched_from: null, backoff_n: n, next_attempt_at: at, last_error: { kind: res.exchange.kind, status } }
      break
    default:
      next = { ...inflight, state: OP_STATE.failedPermanent, dispatched_from: null, last_error: { kind: res.exchange.kind, status } }
      stop = true
  }
  await settle(deps, rec.scope.id, (latest) => (stop
    // Stopped for the user (finding 7): the draft keeps its text and the error.
    ? { putRecords: [next], draft: latest ? { ...latest, status: 'failed', last_error: next.last_error } : undefined, event: ev(deps, { type: 'failed', deckId: rec.scope.id }) }
    : { putRecords: [next] }))
  return { sent: 1, again: false }
}

async function settleRead(deps: DeckDrainDeps, rec: OpRecord, userId: string): Promise<Step> {
  if (!stillOwned(deps, rec)) return { sent: 0, again: false }
  const read = await deps.transport.readRow(rec.scope.id, userId)
  if (!stillOwned(deps, rec)) return { sent: 0, again: false }
  const now = deps.now()
  if (!read.ok) {
    const n = (rec.backoff_n ?? 0) + 1
    await settle(deps, rec.scope.id, () => ({ putRecords: [{ ...rec, backoff_n: n, next_attempt_at: rfc3339(new Date(now.getTime() + backoffDelayMs(n, deps.u()))) }] }))
    return { sent: 0, again: false }
  }
  const verdict = settleByRow(rec, read.row)
  switch (verdict) {
    case 'done':
      return onDone(deps, rec, read.row!)
    case 'not_applied': {
      // Resend the SAME frozen request (its rev guard is never rewritten).
      const n = rec.backoff_n ?? 0
      const nextAt = n === 0 ? null : rfc3339(new Date(now.getTime() + backoffDelayMs(n, deps.u())))
      const ok = await settle(deps, rec.scope.id, () => ({ putRecords: [{ ...rec, state: OP_STATE.pending, hold: null, backoff_n: n + 1, next_attempt_at: nextAt }] }))
      return { sent: 0, again: ok && nextAt === null }
    }
    case 'conflict': {
      // The user's "Your deck changed" choice owns it now: the attempt stays
      // ambiguous (held), the draft is kept, nothing rebased, nothing deleted.
      const row = read.row!
      await settle(deps, rec.scope.id, (latest) => ({
        putRecords: [{ ...rec, state: OP_STATE.ambiguous, hold: { reason: HOLD.conflict, since: rfc3339(now) } }],
        draft: latest ? { ...latest, status: 'conflict', conflict_row: row } : undefined,
        event: ev(deps, { type: 'conflict', deckId: rec.scope.id, row }),
      }))
      return { sent: 0, again: false }
    }
    case 'missing': {
      await settle(deps, rec.scope.id, (latest) => ({
        putRecords: [{ ...rec, state: OP_STATE.failedPermanent, last_error: { kind: 'missing', code: 'missing' } }],
        draft: latest ? { ...latest, status: 'missing' } : undefined,
        event: ev(deps, { type: 'missing', deckId: rec.scope.id }),
      }))
      return { sent: 0, again: false }
    }
  }
}

/**
 * The attempt landed: the ONLY acknowledgement. In ONE transaction the
 * attempt goes and the LATEST draft is rebased onto the returned row and
 * compared: equal -> cleared; different -> a successor next step.
 */
async function onDone(deps: DeckDrainDeps, rec: OpRecord, row: DeckRow): Promise<Step> {
  let cleared = true
  const now = deps.now()
  const ok = await settle(deps, rec.scope.id, (latest) => {
    let draft: DeckDraft | null | undefined
    if (latest) {
      const rebased = rebaseOnto(latest, row, now)
      if (draftSatisfiedBy(rebased, row)) draft = null
      else {
        draft = rebased
        cleared = false
      }
    }
    return { delRecords: [rec.op_id], draft, event: ev(deps, { type: 'done', deckId: rec.scope.id, row, cleared }) }
  })
  if (!ok) return { sent: 0, again: false }
  return { sent: 0, again: !cleared }
}

/** recovered / session_restored: every waiting deck attempt is due now. */
export async function makeDeckAttemptsDue(ctx: WriterCtx): Promise<void> {
  const q = (await readQueue(ctx.ownerKey)).filter(r => isDeckAttempt(r) && r.next_attempt_at)
  if (q.length === 0) return
  await fencedWrite(ctx, [STORE.queue], tx => {
    const s = tx.objectStore(STORE.queue)
    for (const r of q) s.put({ ...r, next_attempt_at: null }, r.op_id)
  })
}

export function __resetDeckOutboxForTests(): void {
  listeners.clear()
}

/** Test seam: deliver an event as a committed settlement would. */
export function __emitDeckOutboxForTests(e: OutboxEvent): void {
  emit(e)
}
